import { debounce } from 'lodash'
import { Controller } from 'stimulus'

const invisible = 'ㅤ'

function template(tpl, numItems, contents) {
  return `<b>${numItems}) MANIPULADO</b>
    ${tpl.formula_ingredients
      .map((i) => {
        const ingredient = (i.ingredient ?? '').trim()
        const measure = (i.measure ?? '').trim()
        const included = contents.includes(ingredient)
        const repeated = `<del>${ingredient} | ${measure}</del>`
        const normal = `${ingredient} | ${measure}`
        const line = included && (!ingredient.toLowerCase().includes('qsp')) ? repeated : normal
        return `<div>${line.trim()}</div>`
      })
      .join('\n')}
      <b>Posologia</b>: ${tpl.instructions.trim()} ${
    tpl.duration ? `por ${tpl.duration} dias.`.trim() : invisible
  }`.trim()
}

function highlight(val, search) {
  if (search.length < 3) {
    return val
  }

  return val.replace(new RegExp(search, 'gi'), (v) => {
    return `<span class="highlight">${v}</span>`
  })
}

export default class PrescriptionsController extends Controller {
  static targets = [
    'selectedTemplates',
    'rawContents',
    'protocolsPreview',
    'medicinesPreview',
    'supplementsPreview',
    'formulasPreview',
    'protocolDraftToggle',
    'protocolDraftTitle',
    'duration',
    'form'
  ]

  connect() {
    const trixes = document.querySelectorAll('trix-editor')
    this.trix = trixes[trixes.length - 1]

    this.trix.addEventListener('trix-change', (e) => {
      const plainText = this.trix.editor.getDocument().toString()

      try {
        if (this.rawContentsTarget) {
          this.rawContentsTarget.value = plainText
        }
      } catch (err) {
        console.warn(err)
      }
    })

    this.controller = new AbortController()

    document
      .querySelector('#duration')
      ?.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          this.durationChange()
          return false
        }
      })

    this.formTarget.addEventListener('keypress', (event) => {
      if (event.key === 'Enter' && event.target.tagName !== 'TRIX-EDITOR') {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    })
  }

  durationChange() {
    if (!this.isDurationSet()) {
      return
    }

    const val = this.trix.editor.element.value
    let newVal = val.replace(
      /por \d+ dias\./g,
      `por ${this.getDuration()} dias.`
    )

    newVal = newVal.replace(
      new RegExp(invisible, 'g'),
      `por ${this.getDuration()} dias.`
    )

    this.trix.editor.loadHTML(newVal)
  }

  isDurationSet() {
    try {
      return !this.durationTarget.classList.contains('hidden')
    } catch (e) {
      return false
    }
  }

  getDuration() {
    try {
      const duration = this.durationTarget.querySelector(
        'input[type="number"]'
      ).value

      return duration
    } catch (err) {
      return null
    }
  }

  toggleDuration() {
    this.durationTarget.classList.toggle('hidden')
    this.durationChange()
  }

  toggleFormulas() {
    this.formulasPreviewTarget.classList.toggle('hidden')
  }

  toggleProtocols() {
    this.protocolsPreviewTarget.classList.toggle('hidden')
  }

  toggleMedicines() {
    this.medicinesPreviewTarget.classList.toggle('hidden')
  }

  toggleSupplements() {
    this.supplementsPreviewTarget.classList.toggle('hidden')
  }

  appendContent(text) {
    let val = this.trix.editor.element.value
    val += `${text}`
    this.trix.editor.loadHTML(val)

    const length = this.trix.editor.getDocument().toString().length
    this.trix.editor.setSelectedRange(length - 1)
    this.trix.editor.insertLineBreak()
  }

  addProtocol(e) {
    e.preventDefault()
    const contents = decodeURIComponent(
      atob(e.currentTarget.getAttribute('data-protocol-contents'))
    )
    this.appendContent(contents)
  }

  addSupplement(e) {
    e.preventDefault()
    const title = decodeURIComponent(
      atob(e.currentTarget.getAttribute('data-supplement-title'))
    )

    const instructions = decodeURIComponent(
      atob(e.currentTarget.getAttribute('data-supplement-instructions'))
    )

    const num = this.numItems()

    this.appendContent(
      `
      <b>${num + 1}) INDUSTRIALIZADO</b>
      <div>${title}</div>
      <div><b>Posologia</b>: ${instructions} ${
        this.getDuration() ? `por ${this.getDuration()} dias.` : invisible
      } </div>`.trim()
    )
  }

  addMedicine(e) {
    e.preventDefault()

    const name = e.currentTarget.getAttribute('data-medicine-name')
    const presentation = e.currentTarget.getAttribute(
      'data-medicine-presentation'
    )
    const num = this.numItems()

    this.appendContent(
      `
      <b>${num + 1}) MEDICAMENTO</b>
      <div>${name} ${presentation}</div>
      <div><b>Posologia</b>: tomar ${
        this.getDuration() ? `por ${this.getDuration()} dias.` : invisible
      } </div>`.trim()
    )
  }

  numItems() {
    const contents = this.trix.editor.getDocument().toString()
    const numFormulas = contents.split('MANIPULADO').length - 1
    const numMedicines = contents.split('MEDICAMENTO').length - 1
    const numSupplements = contents.split('INDUSTRIALIZADO').length - 1
    return numFormulas + numMedicines + numSupplements
  }

  addTemplate(e) {
    e.preventDefault()
    const rawContents = e.currentTarget.getAttribute('data-formula-contents')
    const selected = JSON.parse(decodeURIComponent(atob(rawContents)))

    if (this.isDurationSet()) {
      selected.duration = this.durationTarget.querySelector(
        'input[type="number"]'
      ).value
    }

    const contents = this.trix.editor.getDocument().toString()
    this.appendContent(template(selected, this.numItems() + 1, contents))
  }

  toggleNote(e) {
    e.preventDefault()
    e.stopPropagation()

    const id = e.currentTarget.getAttribute('data-formula-id')
    document.querySelector(`.notes-${id}`).classList.toggle('visible')
    return false
  }

  async toggleFavorite(e) {
    e.preventDefault()
    e.stopPropagation()

    const id = e.currentTarget.getAttribute('data-formula-id')
    document.querySelector(`.favorites-${id}`).classList.toggle('favorite')

    await fetch(`/formulas/${id}/favorite`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        'Content-Type': 'application/json',
      },
    })

    return false
  }

  formulasQuery = (val) => {
    const checked = document.querySelector('input[name="formulas"]').checked
    if (!checked) {
      return
    }

    return fetch(`/formulas.json?search=${val}`, {
      signal: this.controller.signal,
    }).then(async (formulasBody) => {
      const { formulas, favorite_ids } = await formulasBody.json()

      const formulasList = formulas.map((obj) => {
        const tags = obj.tag_names
          .map((tag) => `<span class="tag">${highlight(tag, val)}</span>`)
          .join(' ')

        const ingredients = obj.formula_ingredients
          .map(
            ({ ingredient }) =>
              `<span class="ingredient">${highlight(ingredient, val)}</span>`
          )
          .join(' ')

        return `
      <li data-action="click->prescriptions#addTemplate" data-formula="${
        obj.id
      }" class="formula-row" data-formula-contents=${btoa(
          encodeURIComponent(JSON.stringify(obj))
        )}>
        <div class="search-title">
          <span>${highlight(obj.title, val)}</span>

          <div class="search-actions">
            <div class="search-star favorites-${obj.id} ${
          favorite_ids.includes(obj.id) ? 'favorite' : ''
        }" data-action="click->prescriptions#toggleFavorite" data-formula-id=${
          obj.id
        }>
              <i class="fal fa-star"></i>
            </div>

            ${
              Boolean(obj.notes)
                ? `
                <div class="search-description" data-action="click->prescriptions#toggleNote" data-formula-id=${obj.id}>
                  <i class="fas fa-search"></i>
                </div>`
                : ''
            }
          </div>
        </div>
        <div class="search-tags">${tags}</div>
        <div class="search-ingredients">${ingredients}</div>
      </li>
      ${
        Boolean(obj.notes)
          ? `<div class="notes notes-${obj.id}">
            <span class="triangle"></span>
            <i class="far fa-times close" data-action="click->prescriptions#toggleNote" data-formula-id=${
              obj.id
            }></i>
            <p>
              ${obj.notes.replace(/\n/g, '<br />')}
            </p>
            </div>`
          : ''
      }
      `
      })

      this.formulasPreviewTarget.innerHTML = formulasList.join('\n')
      this.formulasPreviewTarget.style.display = formulas.length
        ? 'block'
        : 'none'
    })
  }

  protocolsQuery = (val) => {
    const checked = document.querySelector('input[name="protocols"]').checked
    if (!checked) {
      return
    }

    return fetch(`/protocols.json?search=${val}`, {
      signal: this.controller.signal,
    }).then(async (protocolsBody) => {
      const protocols = await protocolsBody.json()
      const protocolsList = protocols.map((obj) => {
        const tags = obj.tag_names
          .map((tag) => `<span class="tag">${highlight(tag, val)}</span>`)
          .join(' ')

        return `
        <li data-action="click->prescriptions#addProtocol" data-protocol="${
          obj.id
        }" data-protocol-contents=${btoa(encodeURIComponent(obj.contents))}>
          <span>${highlight(obj.title, val)}</span>
          <div class="search-tags">${tags}</div>
        </li>`
      })
      this.protocolsPreviewTarget.innerHTML = protocolsList.join('\n')
      this.protocolsPreviewTarget.style.display = protocols.length
        ? 'block'
        : 'none'
    })
  }

  medicinesQuery = (val) => {
    const checked = document.querySelector('input[name="medicines"]')?.checked
    if (!checked) {
      return
    }

    return fetch(`/medicines/autocomplete.json?search=${val}&page=1`, {
      signal: this.controller.signal,
    }).then(async (medicinesBody) => {
      const medicines = await medicinesBody.json()

      const medicinesList = medicines.map((obj) => {
        const presentations = obj.clean_presentations
          .map(
            (pres) =>
              `<li data-action="click->prescriptions#addMedicine" 
                    data-medicine-name="${obj.name}"
                    data-medicine-presentation="${pres}"
                  >
                    ${pres}
                </li>`
          )
          .join(' ')

        return `
        <div class="medicine">
          <details>
            <summary>
              <span>${highlight(obj.name, val)}</span>
            </summary>
            <ul class="presentations">${presentations}</ul>
          </details>
        </div>`
      })

      this.medicinesPreviewTarget.innerHTML = medicinesList.join('\n')
      this.medicinesPreviewTarget.style.display = medicines.length
        ? 'block'
        : 'none'
    })
  }

  supplementsQuery = (val) => {
    const checked = document.querySelector('input[name="supplements"]').checked
    if (!checked) {
      return
    }

    return fetch(`/supplements/autocomplete.json?search=${val}`, {
      signal: this.controller.signal,
    }).then(async (supplementsBody) => {
      const supplements = await supplementsBody.json()

      const supplementsList = supplements.map((obj) => {
        const tags = obj.tag_names
          .map((tag) => `<span class="tag">${highlight(tag, val)}</span>`)
          .join(' ')

        return `
        <li data-action="click->prescriptions#addSupplement" data-supplement-title=${btoa(
          encodeURIComponent(obj.title) + ' | ' + encodeURIComponent(obj.source)
        )} data-supplement-instructions=${btoa(
          encodeURIComponent(obj.instructions)
        )}>
          <span>${highlight(obj.title + ' | ' + obj.source, val)}</span>
          <div class="search-ingredients">${tags}</div>
        </li>`
      })

      this.supplementsPreviewTarget.innerHTML = supplementsList.join('\n')
      this.supplementsPreviewTarget.style.display = supplements.length
        ? 'block'
        : 'none'
    })
  }

  search = debounce(async (evt) => {
    const val = evt.target.value

    if (val.trim() === '') {
      this.formulasPreviewTarget.innerHTML = ''
      this.formulasPreviewTarget.style.display = 'none'

      this.protocolsPreviewTarget.innerHTML = ''
      this.protocolsPreviewTarget.style.display = 'none'

      this.medicinesPreviewTarget.innerHTML = ''
      this.medicinesPreviewTarget.style.display = 'none'

      this.supplementsPreviewTarget.innerHTML = ''
      this.supplementsPreviewTarget.style.display = 'none'
      return
    }

    if (
      this.currentFormulasQuery ||
      this.currentMedicinesQuery ||
      this.currentProtocolsQuery ||
      this.currentSupplementsQuery
    ) {
      this.controller.abort()
      this.controller = new AbortController()
    }

    this.currentFormulasQuery = this.formulasQuery(val)
    this.currentProtocolsQuery = this.protocolsQuery(val)
    this.currentMedicinesQuery = this.medicinesQuery(val)
    this.currentSupplementsQuery = this.supplementsQuery(val)

    await Promise.all([
      this.currentFormulasQuery,
      this.currentMedicinesQuery,
      this.currentProtocolsQuery,
      this.currentSupplementsQuery,
    ])
      .catch((err) => {
        if (!err.message.includes('The user aborted a request')) {
          throw err
        }
      })
      .then(() => {
        this.currentFormulasQuery = null
        this.currentProtocolsQuery = null
        this.currentMedicinesQuery = null
        this.currentSupplementsQuery = null
      })
  }, 500)

  enableDraft() {
    const toggleValue =
      this.protocolDraftToggleTarget.querySelector('input').checked

    if (toggleValue) {
      this.protocolDraftTitleTarget.classList.add('visible')
    } else {
      this.protocolDraftTitleTarget.classList.remove('visible')
      this.protocolDraftTitleTarget.querySelector('input').value = ''
    }
  }
}
